import React, {Component} from 'react'
import {
  get,
  find,
  filter,
  includes,
  map,
  has,
  difference,
  omit,
  isEmpty,
  some,
  toUpper,
  sortBy,
  isNil,
  flowRight as compose,
  values,
  last,
} from 'lodash'
import {withStyles} from '@material-ui/core/styles'
import Avatar from '@material-ui/core/Avatar'
import InputLabel from '@material-ui/core/InputLabel'
import FormControl from '@material-ui/core/FormControl'
import Typography from '@material-ui/core/Typography'
import {ADD_ACCOUNT_MUTATION} from '../../../graphql/mutations'
import {CLIENT_DATA_QUERY} from '../../../graphql/queries'
import {graphql} from 'react-apollo'
import FormHelperText from '@material-ui/core/FormHelperText'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import {Trans, withNamespaces} from 'react-i18next'
import messages from '../../../assets/messages'
import LoadingButton from '../../Common/LoadingButton'
import Images from '../../Common/Images'
import {FullScreenDialog, AlertDialog} from '../../Common/Dialog'
import PageTitle from '../../Common/PageTitle'
import PageBody from '../../Common/PageBody'
import PageSubTitle from '../../Common/PageSubTitle'
import classNames from 'classnames'
import {accountTypes, accountSubtypes, currencies, kycStatuses} from '@bdswiss/common-enums'
import {isMobile} from '../../../common/utils/browser'
import AppContext from '../../Common/contexts/AppContext'
import ChangePlan from '../Forex/Plan/ChangePlan'
import {findCompany} from '../../../common/utils'
import {
  getAccountSubtype,
  getAccountSubtypeLabel,
  getAccountTypeForStats,
  getAllowedAccounts,
  isCentAccount,
  isPammInvestorAccount,
  isWalletAccount,
  getAllowedAccountsByCampaignRules,
} from '../../../common/utils/accounts'
import TextField from '@material-ui/core/TextField'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Checkbox from '@material-ui/core/Checkbox'
import ListItemText from '@material-ui/core/ListItemText'
import {config} from '../../../config'
import {validateLength} from '../../../common/utils/validations'
import {validCountries, getMissingDocs} from '../../../common/utils/general'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import MobileStepper from '@material-ui/core/MobileStepper'
import {accountTypeStats} from '../../../common/utils/uioptions'
import CheckIcon from '@material-ui/icons/Check'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import UiNotification from '../../Common/UiNotification'
import CloseIcon from '@material-ui/icons/Close'
import {Password} from '../../Common/Password'
import {Fab} from '@material-ui/core'
import {InnerAppContext} from '../../../common/types'
import MauMT4TermsAndConditionsModal from './MauMT4TermsAndConditionsModal'
import {PammTermsCheckbox} from '../../Common/PammTermsCheckbox'
import {requiresPammTermsAcceptance} from './pammTerms'
import {TauroTermsCheckbox} from '../../Common/TauroTermsCheckbox'
import {TauroRulesModal} from '../../Common/TauroRulesModal'
import {
  getViewerTauroFactorisation,
  requiresTauroTermsAcceptance,
  withoutCentCurrencyForFactorised,
  withTauroClassicOptions,
} from './tauroTerms'
import type {AccountType} from './components/AccountTypeItem'
import {AccountTypeStep} from './components/AccountTypeStep'

const oneCurrencyOnly = config.featuresConfig.oneCurrencyOnly

const styles = (theme) => ({
  cardRoot: {
    padding: 20,
    margin: '20px 0px',
    cursor: 'pointer',
    position: 'relative' as const,
  },
  formControl: {
    display: 'block',
    margin: '20px 0px',
  },
  pointer: {
    cursor: 'pointer',
  },
  passwordTitle: {
    paddingBottom: '30px',
  },
  error: {
    color: theme.palette.error.main,
  },
  button: {
    marginTop: '40px',
    padding: '15px 30px',
  },
  typeBadge: {
    height: 40,
    width: 40,
    fontSize: 15,
    lineHeight: '40px',
    borderRadius: 20,
    textTransform: 'uppercase' as const,
    textAlign: 'center' as const,
    [theme.direction === 'rtl' ? 'marginLeft' : 'marginRight']: 18,
  },
  label: {
    padding: '6px 0px',
  },
  badge: {
    padding: '9px',
    margin: '0px 0px 0px auto',
  },
  currIcon: {
    [theme.direction === 'rtl' ? 'marginLeft' : 'marginRight']: '18px',
  },
  errorMessage: {
    color: theme.palette.error.main,
    display: 'inline-block',
    verticalAlign: 'bottom',
    margin: '13px 13px 13px 0',
  },
  successDiv: {
    backgroundColor: theme.palette.lightgreen.color,
    padding: '15px 10px',
    marginBottom: 40,
  },
  successMessage: {
    color: theme.palette.green.color,
  },
  badgeDiv: {
    paddingTop: 4,
  },
  checkboxLabel: {
    fontSize: 14,
    lineHeight: 1.5,
    [theme.breakpoints.down('sm')]: {
      fontSize: 13,
    },
  },
  link: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontWeight: 400,
  },
  basic: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.lightblue.color,
  },
  raw: {
    color: theme.palette.cyan.color,
    backgroundColor: theme.palette.lightcyan.color,
  },
  firstChildNoMargin: {
    '& > *': {
      margin: 0,
    },
  },
  black: {
    color: theme.palette.purple.color,
    backgroundColor: theme.palette.lightpurple.color,
  },
  invest_plus: {
    color: theme.palette.harlequin.color,
    backgroundColor: theme.palette.lightharlequin.color,
  },
  pammForex: {
    color: theme.palette.green.color,
    backgroundColor: theme.palette.lightgreen.color,
  },
  premium: {
    color: theme.palette.premium.color,
    backgroundColor: theme.palette.lightpremium.color,
  },
  stepper: {
    width: '100%',
    paddingLeft: 0,
    paddingRight: 0,
  },
  buttonCompare: {
    fontSize: 16,
    float: 'right' as const,
    paddingRight: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:active': {
      backgroundColor: theme.palette.background.default,
    },
    [theme.breakpoints.down('sm')]: {
      float: 'none' as const,
      width: '100%',
      padding: 0,
    },
  },
  labelButton: {
    alignItems: 'initial',
    justifyContent: 'initial',
  },
  dialog: {
    width: '100%',
  },
  helpIcon: {
    color: theme.palette.lightgrey.color,
    marginBottom: '-6px',
    [theme.direction === 'rtl' ? 'marginRight' : 'marginLeft']: 10,
    padding: 2,
  },
  popper: {
    zIndex: 9999,
    backgroundColor: theme.palette.lightgreyBackgroundSolid.color,
    padding: '10px 20px',
    boxShadow: theme.boxShadowStyle,
    width: 500,
    [theme.breakpoints.down('xs')]: {
      width: 'auto',
    },
  },
  subtitlePopper: {
    paddingTop: 10,
  },
  checkIcon: {
    color: theme.palette.green.color,
    marginBottom: -7,
    [theme.direction === 'rtl' ? 'marginLeft' : 'marginRight']: 10,
    width: 16,
  },
  buttonPopper: {
    background: 'none',
    position: 'absolute' as const,
    top: 0,
    [theme.direction === 'rtl' ? 'left' : 'right']: 0,
    boxShadow: 'none',
    color: theme.palette.black.color,
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
    '&:active': {
      backgroundColor: theme.palette.background.default,
    },
  },
  cent: {
    color: theme.palette.red.color,
    backgroundColor: theme.palette.lightred.color,
  },
})

export class AddAccount extends Component<any, any> {
  static contextType = AppContext
  context!: InnerAppContext

  constructor(props) {
    super(props)
    const viewer = get(props, 'viewer', {})
    const affCountries = get(viewer, 'affiliateCountries[0].countriesPromoted') || []
    this.state = {
      accountType: '',
      currency: config.featuresConfig.oneCurrencyOnly || '',
      password: '',
      confirmationPassword: '',
      showCurrentPassword: false,
      showConfirmationPassword: false,
      messageErrorPassword: '',
      messageErrorConfirmPassword: '',
      loading: false,
      status: '',
      step: 0,
      token: '',
      selectAllAffCountries: false,
      formPartnership: {
        skypeId: get(viewer, 'skypeId') || '',
        website: get(viewer, 'website') || '',
        affiliateCountries: map(affCountries, (c) => toUpper(c)),
        isCorporate: '',
        acceptTerms: '',
      },
      errorsPartnership: {},
      websiteLength: false,
      companyObject: findCompany(),
      openPopper: {},
      showPartnerMessage: false,
      mT4TnCModalOpen: false,
      showPasswordUpdatePopup: false,
      pammTermsAccepted: false,
      tauroTermsAccepted: false,
      tauroRulesModalOpen: false,
      // Which MT5 Classic option the client picked; undefined until they pick one, and for
      // every account type the 10x choice does not apply to (PT-6510).
      tauroFactorisationRequested: undefined,
    }
  }

  /** PT-5857: PAMM money manager creation requires explicit T&C acceptance. */
  requiresPammTerms() {
    return requiresPammTermsAcceptance(this.state.accountType)
  }

  /** PT-5492: a Tauro 10x factorised account requires explicit T&C acceptance. */
  requiresTauroTerms() {
    return requiresTauroTermsAcceptance(
      this.props.viewer,
      this.state.accountType,
      this.state.accountSubtype,
      this.state.tauroFactorisationRequested,
    )
  }

  handleChangePassword(prop, event) {
    this.setState(
      {[prop]: event.target.value, messageErrorPassword: '', messageErrorConfirmPassword: ''},
      this.checkPassword,
    )
  }

  checkPassword() {
    const {t} = this.props
    const {password, confirmationPassword} = this.state
    if (confirmationPassword && password !== confirmationPassword)
      this.setState({
        messageErrorConfirmPassword: t(
          messages.passwordsNotSame.i18nKey,
          messages.passwordsNotSame.defaults,
        ),
      })
    else this.setState({messageErrorConfirmPassword: ''})
  }

  handleChangeForm(prop, value) {
    if (
      prop === 'affiliateCountries' &&
      value.indexOf('selectAll') !== -1 &&
      !this.state.selectAllAffCountries
    )
      this.setState((state) => ({
        formPartnership: {
          ...state.formPartnership,
          affiliateCountries: map(validCountries(), (country) => country.value),
        },
        errorsPartnership: {...state.errorsPartnership, affiliateCountries: !value},
        selectAllAffCountries: true,
      }))
    else if (
      prop === 'affiliateCountries' &&
      value.indexOf('selectAll') !== -1 &&
      this.state.selectAllAffCountries
    )
      this.setState((state) => ({
        formPartnership: {...state.formPartnership, affiliateCountries: []},
        errorsPartnership: {...state.errorsPartnership, affiliateCountries: !value},
        selectAllAffCountries: false,
      }))
    else
      this.setState((state) => ({
        formPartnership: {...state.formPartnership, [prop]: value},
        errorsPartnership: {...state.errorsPartnership, [prop]: !value, websiteLength: false},
      }))
  }

  checkForm(fields) {
    const {registrationFields} = config
    const exceptions = ['currency']
    const validateFields = difference(fields, registrationFields['default'])
    const {formPartnership} = this.state
    const errorsPartnership = {}
    const partnership = formPartnership
    for (const field in formPartnership) {
      if (field === 'acceptTerms') {
        errorsPartnership[field] = isEmpty(formPartnership[field])
      } else if (includes(validateFields, field)) {
        if (field === 'website') {
          errorsPartnership[field] = isEmpty(formPartnership[field])
          errorsPartnership['websiteLength'] = !validateLength(formPartnership[field], 150)
        } else {
          errorsPartnership[field] = isEmpty(formPartnership[field])
        }
      } else {
        partnership[field] = ''
      }
    }

    if (some(omit(errorsPartnership, exceptions))) {
      this.setState({errorsPartnership})
      return
    }
    this.addAccount(partnership)
  }

  handleClickShowPassword(prop) {
    this.setState((state) => ({[prop]: !state[prop]}))
  }

  handleKeyDown(prop, event) {
    if (event.key === 'Tab') {
      event.preventDefault()
      document.getElementById(prop)?.focus()
    }
  }

  acceptMT4Terms() {
    this.setState((prev) => ({
      mT4TnCModalOpen: false,
    }))
    const formPartnership = this.state.formPartnership
    this.addAccount(formPartnership)
  }

  discardMT4Terms() {
    this.setState({mT4TnCModalOpen: false})
  }

  handleSubmit = () => {
    // Belt and braces: the button is already disabled, but keyboard submit
    // (handleKeyDown on the confirmation password) reaches here directly.
    if (this.requiresPammTerms() && !this.state.pammTermsAccepted) return
    if (this.requiresTauroTerms() && !this.state.tauroTermsAccepted) return

    const showMT4TnCModal = get(this.props, 'location.state.showMT4TnCModal', false)

    if (showMT4TnCModal && this.state.accountType === 'ForexClassicTauroMarketsMauritiusAccount') {
      this.setState({
        mT4TnCModalOpen: true,
      })
    } else {
      const formPartnership = this.state.formPartnership
      this.addAccount(formPartnership)
    }
  }

  addAccount = (formPartnership) => {
    const {
      accountType,
      currency,
      password,
      messageErrorConfirmPassword,
      confirmationPassword,
      token,
    } = this.state
    let {accountSubtype} = this.state
    const onlyPartnerAccount = isWalletAccount(accountType)
    const isIb = accountTypes[accountType].category === 'ib'
    const {t, viewer} = this.props
    const missingPofOrPoi = values(getMissingDocs(viewer)).some((d) => d === true)
    if (
      (password && confirmationPassword && !messageErrorConfirmPassword) ||
      (get(accountTypes[accountType], 'walletProduct', '') && !isIb)
    ) {
      this.setState({status: '', loading: true})
      if (
        get(accountTypes[accountType], 'supportedSubtypes') &&
        accountTypes[accountType].supportedSubtypes.length === 1
      ) {
        accountSubtype = get(
          find(accountSubtypes, (t) => t.value === accountTypes[accountType].supportedSubtypes[0]),
          'key',
        ) //MT5 add subtype for max allowed check
      }
      this.props
        .addAccountMutation({
          variables: {
            password,
            currency,
            accountType,
            accountSubtype,
            tauroTermsAccepted: this.requiresTauroTerms() ? this.state.tauroTermsAccepted : undefined,
            tauroFactorisationRequested: this.state.tauroFactorisationRequested,
            args: JSON.stringify({
              tokenId: token,
              countriesPromoted: formPartnership['affiliateCountries'],
              website: formPartnership.website,
              skypeId: formPartnership.skypeId,
              isCorporate: formPartnership.isCorporate,
            }),
          },
        })
        .then(({data}) => {
          if (get(data, 'account')) {
            if (onlyPartnerAccount && missingPofOrPoi) {
              this.setState({showPartnerMessage: true})
            } else {
              this.setState({loading: false, status: 'success', submitMessageError: ''})
              window.location.href = '/accounts?accountCreated=true'
            }
          } else {
            this.setState({
              loading: false,
              status: 'failure',
              submitMessageError: t(
                messages.somethingWrongError.i18nKey,
                messages.somethingWrongError.defaults,
              ),
            })
          }
        })
        .catch((err) => {
          if (err.networkError) {
            this.setState({
              loading: false,
              status: 'failure',
              submitMessageError: t(messages.networkError.i18nKey, messages.networkError.defaults),
            })
          } else {
            const error = get(err, 'graphQLErrors[0].message') || err.message
            this.setState({loading: false, status: 'failure', submitMessageError: error})
          }
        })
    }
    if (!confirmationPassword)
      this.setState({
        messageErrorConfirmPassword: t(
          messages.requiredField.i18nKey,
          messages.requiredField.defaults,
        ),
      })
    if (!password)
      this.setState({
        messageErrorPassword: t(messages.requiredField.i18nKey, messages.requiredField.defaults),
      })
  }

  chooseAccount(accountType, accountSubtype, tauroFactorised?: boolean) {
    const {accounts} = this.context

    // Both MT5 Classic options share one subtype, so the picked variant is the only thing
    // that tells the 10x account apart from the standard one (PT-6510).
    this.setState({tauroFactorisationRequested: tauroFactorised})

    const MT5_SVG_TAURO_ACCOUNT_TYPES = [
      'ForexMT5SVGTauromarketsAccount',
      'ForexMT5SVGTauromarketsDemoAccount',
    ]

    const {viewer} = this.props

    const creatingMt5SVGAccount = MT5_SVG_TAURO_ACCOUNT_TYPES.includes(accountType)
    const hasMt5SVGAccount = accounts.some((a) =>
      MT5_SVG_TAURO_ACCOUNT_TYPES.includes(a.__typename),
    )

    if (
      creatingMt5SVGAccount &&
      !hasMt5SVGAccount &&
      !viewer.isResetClientPassword &&
      viewer.whiteLabel === 'tauroMarkets'
    ) {
      this.setState({showPasswordUpdatePopup: true})
    }

    this.setState({accountType: accountType})
    if (isCentAccount({__typename: accountType})) {
      this.setState({currency: currencies.CUD.value})
    }
    if (isPammInvestorAccount({__typename: accountType})) {
      this.setState({currency: this.props.viewer.affiliatePAMMCurrency}, this.increaseStep)
    }
    !accountSubtype || accountSubtype === 'demo'
      ? this.setState({accountSubtype: undefined}, this.increaseStep)
      : this.setState({accountSubtype: accountSubtype}, this.increaseStep)
  }

  chooseCurrency(props) {
    this.setState({currency: props}, this.increaseStep)
  }

  decreaseStep() {
    const {history} = this.props
    const {step, status, accountType} = this.state

    this.setState({openPopper: {}})
    if (step === 1) {
      this.setState({accountType: '', accountSubtype: undefined, tauroFactorisationRequested: undefined})
    }
    if (step === 2 && isPammInvestorAccount({__typename: accountType})) {
      this.setState({
        step: 0,
        accountType: '',
        accountSubtype: undefined,
      })
      return
    }
    step > 0 && !status ? this.setState({step: step - 1}) : history.push('/')
  }

  increaseStep() {
    this.setState((state) => ({step: ++state.step}))
  }

  renderForm() {
    const {classes} = this.props
    const {
      showCurrentPassword,
      password,
      showConfirmationPassword,
      confirmationPassword,
      messageErrorConfirmPassword,
      messageErrorPassword,
      loading,
      status,
      submitMessageError,
      mT4TnCModalOpen,
      pammTermsAccepted,
      tauroTermsAccepted,
    } = this.state
    const needsPammTerms = this.requiresPammTerms()
    const needsTauroTerms = this.requiresTauroTerms()

    return (
      <Grid container>
        <Grid item xs={12} sm={10}>
          <PageSubTitle>
            <Trans {...messages.enterPassword} />
            <Typography variant="caption">
              <Trans {...messages.addAccountPassDesc} />
            </Typography>
          </PageSubTitle>
          <Password
            classes={classes}
            showPassword={showCurrentPassword}
            onChange={(e) => this.handleChangePassword('password', e)}
            onKeyDown={(e) => this.handleKeyDown('confirmationPassword', e)}
            error={messageErrorPassword}
            onClickShow={() => this.handleClickShowPassword('showCurrentPassword')}
            fullWidth
            errorText={messageErrorPassword}
            value={password}
          />
          <Password
            id={'confirmationPassword'}
            classes={classes}
            showPassword={showConfirmationPassword}
            onChange={(e) => this.handleChangePassword('confirmationPassword', e)}
            onKeyDown={(e) => this.handleKeyDown('loadingButton', e)}
            error={messageErrorConfirmPassword}
            onClickShow={() => this.handleClickShowPassword('showConfirmationPassword')}
            fullWidth
            errorText={messageErrorConfirmPassword}
            label={'passwordConfirmation'}
            value={confirmationPassword}
          />
          {needsPammTerms && (
            <PammTermsCheckbox
              checked={pammTermsAccepted}
              disabled={loading}
              onChange={(checked) => this.setState({pammTermsAccepted: checked})}
            />
          )}
          {needsTauroTerms && (
            <>
              <Typography variant="caption" paragraph id="tauroTermsDescription">
                <Trans {...messages.tauroTermsDescription} />
              </Typography>
              <TauroTermsCheckbox
                checked={tauroTermsAccepted}
                disabled={loading}
                onChange={(checked) => this.setState({tauroTermsAccepted: checked})}
                onOpenRules={() => this.setState({tauroRulesModalOpen: true})}
              />
            </>
          )}
          <LoadingButton
            id="loadingButton"
            onClick={this.handleSubmit}
            fullWidth={isMobile()}
            disabled={loading || (needsPammTerms && !pammTermsAccepted) || (needsTauroTerms && !tauroTermsAccepted)}
            status={status}
          >
            <Trans {...messages.addAccount} />
          </LoadingButton>
          {status === 'failure' && (
            <FormHelperText className={classes.errorMessage}>{submitMessageError}</FormHelperText>
          )}
        </Grid>
        <MauMT4TermsAndConditionsModal
          open={mT4TnCModalOpen}
          onSubmit={this.acceptMT4Terms.bind(this)}
          onCancel={this.discardMT4Terms.bind(this)}
        />
        {needsTauroTerms && (
          <TauroRulesModal
            open={this.state.tauroRulesModalOpen}
            onClose={() => this.setState({tauroRulesModalOpen: false})}
            rules={getViewerTauroFactorisation(this.props.viewer)}
          />
        )}
      </Grid>
    )
  }

  renderDetailsForm() {
    const {classes, t} = this.props
    const {
      registrationFields,
      weblinks: {
        termsAndConditions,
        legalDocuments,
        privacyPolicy,
        affiliateTermsAndConditions,
        affiliateGuidelines,
        affiliateCodeConduct,
        ibGuidelines,
        ibCodeConduct,
        ibAppendix,
        ibMasterTermsAndConditions,
      },
      translatedPartnerGuidelines,
    } = config
    const {
      accountType,
      formPartnership: {skypeId, website, affiliateCountries, isCorporate},
      selectAllAffCountries,
      submitMessageError,
      loading,
      status,
      companyObject,
      acceptTerms,
      showCurrentPassword,
      password,
      showConfirmationPassword,
      confirmationPassword,
      messageErrorPassword,
      messageErrorConfirmPassword,
    } = this.state
    const accountTypeObj = accountTypes[accountType]
    const fields =
      (has(registrationFields, accountTypeObj.category) ||
        has(registrationFields, accountTypeObj.subCategory)) &&
      (registrationFields[accountTypeObj.category]
        ? registrationFields[accountTypeObj.category]
        : registrationFields[accountTypeObj.subCategory])
    const {locale} = this.context
    const isAffiliate = includes(accountTypeObj.category, 'affiliate')
    const isIb = accountTypeObj.category === 'ib'

    return (
      <Grid container spacing={3}>
        <PageSubTitle>
          <Trans {...messages.moreDetails} />
          <Typography variant="caption">
            <Trans {...messages.addAccountDetailsDesc} />
          </Typography>
        </PageSubTitle>
        {includes(fields, 'skypeId') && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              id="skypeId"
              label={t(messages.skypeId.i18nKey, messages.skypeId.defaults)}
              onChange={(e) => this.handleChangeForm('skypeId', e.target.value)}
              error={this.state.errorsPartnership.skypeId}
              value={skypeId}
            />
          </Grid>
        )}
        {includes(fields, 'website') && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              id="website"
              label={t(messages.website.i18nKey, messages.website.defaults)}
              onChange={(e) => this.handleChangeForm('website', e.target.value)}
              error={
                this.state.errorsPartnership.website || this.state.errorsPartnership.websiteLength
              }
              value={website}
            />
            {this.state.errorsPartnership.websiteLength && (
              <FormHelperText className={classes.error}>
                <Trans {...messages.maxLengthError} values={{length: 150}} />
              </FormHelperText>
            )}
          </Grid>
        )}
        {includes(fields, 'affiliateCountries') && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel
                htmlFor="affiliateCountries"
                error={this.state.errorsPartnership.affiliateCountries}
                required
              >
                {t(messages.affiliateCountries.i18nKey, messages.affiliateCountries.defaults)}
              </InputLabel>
              <Select
                multiple
                required
                id="affiliateCountries"
                placeholder="affiliateCountries"
                name="affiliateCountries"
                onChange={(e) => this.handleChangeForm('affiliateCountries', e.target.value)}
                error={this.state.errorsPartnership.affiliateCountries}
                value={affiliateCountries}
                renderValue={(selected: any) => selected.join(', ')}
                classes={{selectMenu: classes.selectMenu}}
              >
                <MenuItem value={'selectAll'}>
                  <Checkbox checked={selectAllAffCountries} />
                  <ListItemText
                    primary={t(messages.selectAll.i18nKey, messages.selectAll.defaults)}
                  />
                </MenuItem>
                {map(validCountries(), (option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={affiliateCountries.indexOf(option.value) > -1} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        {includes(fields, 'isCorporate') && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel error={this.state.errorsPartnership.isCorporate}>
                <Trans {...messages.company} /> / <Trans {...messages.individual} />
              </InputLabel>
              <Select
                value={isCorporate}
                onChange={(e) => this.handleChangeForm('isCorporate', e.target.value)}
                error={this.state.errorsPartnership.isCorporate}
                required
              >
                <MenuItem key={'company'} value={'true'}>
                  <Trans {...messages.company} />
                </MenuItem>
                <MenuItem key={'individual'} value={'false'}>
                  <Trans {...messages.individual} />
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        {isIb && (
          <>
            <Grid item xs={12} className={classes.firstChildNoMargin}>
              <Password
                classes={classes}
                showPassword={showCurrentPassword}
                onChange={(e) => this.handleChangePassword('password', e)}
                onKeyDown={(e) => this.handleKeyDown('confirmationPassword', e)}
                error={messageErrorPassword}
                onClickShow={() => this.handleClickShowPassword('showCurrentPassword')}
                fullWidth
                errorText={messageErrorPassword}
                value={password}
              />
            </Grid>
            <Grid
              item
              xs={12}
              className={classes.firstChildNoMargin}
              style={{marginBottom: '12px'}}
            >
              <Password
                id={'confirmationPassword'}
                classes={classes}
                showPassword={showConfirmationPassword}
                onChange={(e) => this.handleChangePassword('confirmationPassword', e)}
                onKeyDown={(e) => this.handleKeyDown('loadingButton', e)}
                error={messageErrorConfirmPassword}
                onClickShow={() => this.handleClickShowPassword('showConfirmationPassword')}
                fullWidth
                errorText={messageErrorConfirmPassword}
                label={'passwordConfirmation'}
                value={confirmationPassword}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <FormControl>
            <FormControlLabel
              classes={{label: classes.checkboxLabel}}
              control={
                <Checkbox
                  checked={this.state.acceptTerms}
                  onChange={(e) =>
                    this.handleChangeForm('acceptTerms', e.target.checked ? 'accept' : '')
                  }
                  color="primary"
                  value={acceptTerms}
                  className={this.state.errorsPartnership.acceptTerms ? classes.error : ''}
                />
              }
              label={
                isAffiliate ? (
                  <Trans
                    {...messages.signUpAffiliateCheckbox}
                    components={[
                      <a
                        href={termsAndConditions.replace('{lang}', locale)}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        terms
                      </a>,
                      <a
                        href={legalDocuments.replace('{lang}', locale)}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        documents
                      </a>,
                      <a
                        href={privacyPolicy.replace('{lang}', locale)}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        data
                      </a>,
                      <a
                        href={affiliateTermsAndConditions}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        terms
                      </a>,
                      <a
                        href={affiliateCodeConduct}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        code
                      </a>,
                      <a
                        href={
                          includes(translatedPartnerGuidelines, locale)
                            ? affiliateGuidelines.replace('.pdf', `_${locale.toUpperCase()}.pdf`)
                            : affiliateGuidelines
                        }
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        guideliness
                      </a>,
                    ]}
                  />
                ) : (
                  <Trans
                    {...messages.signUpIBCheckbox}
                    values={{company: companyObject.brandLabel}}
                    components={[
                      <a
                        href={termsAndConditions.replace('{lang}', locale)}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        terms
                      </a>,
                      <a
                        href={legalDocuments.replace('{lang}', locale)}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        documents
                      </a>,
                      <a
                        href={privacyPolicy.replace('{lang}', locale)}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        data
                      </a>,
                      <a
                        href={ibCodeConduct}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        code
                      </a>,
                      <a
                        href={
                          includes(translatedPartnerGuidelines, locale)
                            ? ibGuidelines!.replace('.pdf', `_${locale.toUpperCase()}.pdf`)
                            : ibGuidelines
                        }
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        guideliness
                      </a>,
                      <a
                        href={affiliateTermsAndConditions}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        ibTerms
                      </a>,
                      <a
                        href={ibMasterTermsAndConditions}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        ibMasterTerms
                      </a>,
                      <a
                        href={ibAppendix}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={classes.link}
                      >
                        appendix
                      </a>,
                    ]}
                  />
                )
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <LoadingButton
            id="loadingButtonPartnership"
            onClick={() => this.checkForm(fields)}
            fullWidth={isMobile()}
            disabled={loading}
            status={status}
          >
            <Trans {...messages.addAccount} />
          </LoadingButton>
          {status === 'failure' && (
            <FormHelperText className={classes.errorMessage}>{submitMessageError}</FormHelperText>
          )}
        </Grid>
      </Grid>
    )
  }

  clickOutListener(e) {
    if (
      !includes(get(e, 'toElement.id'), 'helpIcon') &&
      !includes(get(e, 'target.id'), 'helpIcon') &&
      !includes(get(e, 'target.ownerSVGElement.id'), 'helpIcon')
    )
      this.setState({openPopper: {}})
  }

  getAccountDisplayLabel(account) {
    const {viewer} = this.props
    if (!account) return ''
    const accountSubtype = getAccountSubtype(account.accountSubtype as any)
    return getAccountSubtypeLabel(accountSubtype, viewer) || account.label
  }

  renderAccountDetails(account) {
    const {classes, t} = this.props
    const accountTypeForStats = getAccountTypeForStats(account)
    if (!accountTypeForStats) return
    return (
      <ClickAwayListener onClickAway={(e) => this.clickOutListener(e)}>
        <Grid container spacing={0} onClick={(e) => e.stopPropagation()}>
          <Fab className={classes.buttonPopper} onClick={() => this.setState({openPopper: {}})}>
            <CloseIcon />
          </Fab>
          <Grid item xs={12}>
            <Typography variant="body2">{this.getAccountDisplayLabel(account)}</Typography>
          </Grid>
          {map(get(accountTypeStats[accountTypeForStats], 'details'), (value, key) => (
            <Grid container key={key}>
              <Grid item xs={6}>
                <Typography variant="body1">{t(messages[key].i18nKey)}:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body1">
                  {t(messages[`${key}Text`].i18nKey, {value})}
                </Typography>
              </Grid>
            </Grid>
          ))}
          {!isNil(get(accountTypeStats[accountTypeForStats], 'commission')) && (
            <React.Fragment>
              <Grid item xs={12}>
                <Typography variant="body2" className={classes.subtitlePopper}>
                  {t(messages.commission.i18nKey)}:
                </Typography>
              </Grid>
              {map(get(accountTypeStats[accountTypeForStats], 'commission'), (value, key) => {
                const country = get(this.props.viewer, 'address.country', '')
                const commissionValue = get(value, `${country}.value`) || value.value
                return (
                  <Grid container key={key}>
                    <Grid item xs={6}>
                      <Typography variant="body1">{t(messages[key].i18nKey)}:</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1">
                        {t(messages[value.text].i18nKey, {value: commissionValue})}
                      </Typography>
                    </Grid>
                  </Grid>
                )
              })}
            </React.Fragment>
          )}
          <Grid item xs={12}>
            <Typography variant="body2" className={classes.subtitlePopper}>
              {t(messages.benefits.i18nKey)}:
            </Typography>
          </Grid>
          {map(get(accountTypeStats[accountTypeForStats], 'benefits'), (benefit) => (
            <Grid item xs={12} key={benefit}>
              <Typography variant="body1">
                <CheckIcon className={classes.checkIcon} />
                {t(messages[benefit].i18nKey)}
              </Typography>
            </Grid>
          ))}
          {get(accountTypeStats[account.accountSubtype], 'note') && (
            <Grid item xs={12}>
              <Typography variant="body1" className={classes.subtitlePopper}>
                {t(messages[get(accountTypeStats[account.accountSubtype], 'note')].i18nKey)}
              </Typography>
            </Grid>
          )}
        </Grid>
      </ClickAwayListener>
    )
  }

  getCurrenciesList() {
    const {accountType} = this.state
    const {viewer} = this.props
    let currenciesList: any = []
    if (accountType && accountTypes[accountType].disallowedCurrencies) {
      currenciesList = filter(
        currencies,
        (p) => !includes(accountTypes[accountType].disallowedCurrencies, p.key),
      )
        // TODO@jkica: should we keep this, since it was implemented for a specific temporary change? (SKYG-776)
        .filter(
          (currency) =>
            !config.visual?.accounts?.allowedAccountCurrencies ||
            config.visual?.accounts?.allowedAccountCurrencies.includes(currency.key),
        )
      // USC is a gated cent currency (epic PT-5525): only surface it on live MT5 account types and
      // only when the client is actually allowed to open one (kill-switch on, soft-launch allowlist,
      // and the campaign opted in — resolved server-side via canOpenUscAccount). It is not offered
      // for demo accounts (PT-5558). Strip it otherwise so the dropdown never offers what the
      // account-creation API would reject.
      const isMt5AccountType = accountTypes[accountType].category === 'forexMt5'
      const isDemoAccountType = Boolean(accountTypes[accountType].isDemo)
      // PT-5559: the MT5 PAMM manager accounts also run on MT5 and can hold a USC balance, but their
      // category is 'pamm' (shared with the MT4 PAMM manager), so match the types explicitly. Both
      // USC-supported white-labels are covered: SVG and Mauritius (the Mauritius PAMM manager is MT5 too).
      const mt5PammManagerValues = [
        accountTypes.PAMMTauroMarketsMT5ManagerAccount.value,
        accountTypes.PAMMTauroMarketsMauritiusManagerAccount.value,
      ]
      const isMt5PammManagerAccountType = mt5PammManagerValues.includes(accountTypes[accountType].value)
      if (
        (!isMt5AccountType && !isMt5PammManagerAccountType) ||
        isDemoAccountType ||
        !viewer?.canOpenUscAccount
      ) {
        currenciesList = currenciesList.filter((currency) => currency.key !== 'USC')
      }
      currenciesList = withoutCentCurrencyForFactorised(
        currenciesList,
        viewer,
        accountType,
        this.state.accountSubtype,
        this.state.tauroFactorisationRequested,
      )
      const restrictedCurrencyIbIds = [10508, 10512, 10512, 10544, 10543, 10542]
      let remoteIbId: number | undefined
      if (viewer?.metadata) {
        try {
          remoteIbId = JSON.parse(viewer.metadata)?.ibFollowerData?.remoteIbId
        } catch (err) {
          console.warn('Failed to parse metadata:', viewer.metadata)
        }
      }
      if (
        restrictedCurrencyIbIds.includes(viewer?.affiliateId ?? 0) ||
        (remoteIbId != null && restrictedCurrencyIbIds.includes(remoteIbId))
      ) {
        currenciesList = currenciesList.filter((currency) => currency.key === 'USD')
      }
      currenciesList = map(currenciesList, (o: any) => ({
        ...o,
        label: `${o.label} ( ${o.symbol} )`,
        // Virtual cent currencies (e.g. USC) have no flag of their own — reuse their base
        // currency's flag so USC shows the USD/USA flag instead of a placeholder (PT-5558).
        icon: Images[
          `${(currencies[o.key]?.isVirtualCurrency
            ? currencies[o.key]?.baseCurrency
            : o.key
          )?.toLowerCase()}.png`
        ],
      }))
    }
    return currenciesList
  }

  getAccountsCategoryList(accountTypesList) {
    const {accounts} = this.context
    const {accountSubtype, accountType: typeKey} = this.state
    if (!accounts) return []
    const allowedAccountsList: any[] = []
    const type: any = find(accountTypes, {key: typeKey})
    const selectedAccountType = find(
      accountTypesList,
      (l) => l.accountSubtype === accountSubtype && l.isDemo === type.isDemo,
    )
    selectedAccountType &&
      map(selectedAccountType.accountTypesList, (type) => {
        const accountType = get(accountTypes, type)
        if (accountType) {
          const account = {
            label: `${accountType.label}`,
            icon: accountType.shortLabel,
            accountType: accountType.key,
            accountSubtype: accountSubtype,
            category: accountType.category,
            subCategory: accountType.subCategory || '',
            baseProduct:
              accountType.baseProduct &&
              accountType.baseProduct.replace(/^\w/, (c) => c.toLowerCase()),
            walletProduct: accountType.walletProduct,
            isDemo: accountType.isDemo,
          }
          allowedAccountsList.push(account)
        }
      })
    return sortBy(allowedAccountsList, (a) => !!a.walletProduct)
  }

  renderPartnerMessageNotification() {
    const {t, history} = this.props
    const {showPartnerMessage} = this.state
    return (
      <UiNotification
        title={'additionalDocumentsNeeded'}
        open={showPartnerMessage}
        status={'success'}
        type={'document-upload'}
        buttonMessage={t(messages.uploadDocuments.i18nKey)}
        onClose={() => history.push('/accounts?accountCreated=true')}
        buttonAction={() => history.push('/settings/profile/documents')}
        linkActionMessage={t(messages.maybeLater.i18nKey, messages.maybeLater.defaults)}
        linkAction={'/accounts?accountCreated=true'}
      >
        <Trans {...messages.messagePartnerAddAccount} />
      </UiNotification>
    )
  }

  render() {
    const {accounts} = this.context
    const {classes, t, history, viewer} = this.props
    const {disabledAccountCreation} = config

    if (disabledAccountCreation) {
      history.push('/')
    }

    const {step, accountType, companyObject} = this.state
    // const {accountTypesComparisonUrl, key} = config
    const checkSubscription = accountTypes[accountType] && accountTypes[accountType]['subscription']
    const moreDetails = accountTypes[accountType]?.walletProduct
    const allowedAccountTypes = get(viewer, 'allowedAccountTypes') || []
    const accountsList = getAllowedAccounts(
      accounts,
      companyObject,
      t,
      allowedAccountTypes,
      viewer,
    )
    const registrationCampaign = get(last(viewer.registrationCampaigns), 'campaign')
    const allowedAccounts = withTauroClassicOptions<AccountType>(
      getAllowedAccountsByCampaignRules(accountsList, registrationCampaign),
      viewer,
      accounts,
    )

    const missingDocs = values(getMissingDocs(viewer)).some((d) => d === true)

    if (allowedAccounts.length < 1)
      return (
        <UiNotification
          title={'limitReached'}
          open
          status={'failure'}
          type="limit"
          buttonMessage={t(messages.contactUs.i18nKey)}
          onClose={() => history.push('/')}
          buttonAction={() => history.push('/support')}
        >
          <Trans {...messages.maximumAllowedAccounts} />
        </UiNotification>
      )

    let accountCategoryList: any[] = []
    let categoryList
    if (step > 0) {
      accountCategoryList = this.getAccountsCategoryList(allowedAccounts)

      categoryList = map(accountCategoryList, (account: any, key) => (
        <Card
          classes={{root: classes.cardRoot}}
          className={classes.pointer}
          key={key}
          onClick={() => this.chooseAccount(account.accountType, account.accountSubtype)}
        >
          <Grid container>
            <Grid item xs={2} className={classes.badgeDiv}>
              <div
                className={classNames(
                  classes.typeBadge,
                  classes[account.subCategory] || classes[account.category],
                )}
              >
                {account.icon}
              </div>
            </Grid>
            <Grid item xs={10}>
              <Typography variant="subtitle1" className={classes.label}>
                {this.getAccountDisplayLabel(account)}
              </Typography>
              <Typography variant="caption">
                {get(messages, `${account.baseProduct}Desc`)
                  ? t(
                      messages[`${account.baseProduct}Desc`].i18nKey,
                      messages[`${account.baseProduct}Desc`].defaults,
                    )
                  : t(
                      messages[`${account.category}${account.subCategory}Desc`].i18nKey,
                      messages[`${account.category}${account.subCategory}Desc`].defaults,
                    )}
              </Typography>
            </Grid>
          </Grid>
        </Card>
      ))
    }

    const allowedCurrencyList = this.getCurrenciesList()
    const currencyList = allowedCurrencyList.map((currency: any) => (
      <Card
        classes={{root: classes.cardRoot}}
        key={currency.key}
        onClick={() => this.chooseCurrency(currency.key)}
      >
        <Grid container>
          <Avatar alt={currency['icon']} src={currency['icon']} className={classes.currIcon} />
          <Grid item>
            <Typography variant="subtitle1" className={classes.label}>
              {currency['label']}
            </Typography>
          </Grid>
        </Grid>
      </Card>
    ))

    const stepTwoOrOne = !oneCurrencyOnly ? 2 : 1
    const stepThreeOrTwo = !oneCurrencyOnly ? 3 : 2

    const multipleAccountsOrCheckSubscription = accountCategoryList.length > 1 || checkSubscription
    const notCentAccountAndNotSubscription =
      accountCategoryList.length <= 1 &&
      !checkSubscription &&
      !isCentAccount({__typename: accountType})

    const isAccountTypeStep = step === 0

    const categoryStep =
      (step === 1 && accountCategoryList.length > 1) || (step === 1 && checkSubscription)

    const currenciesStep =
      ((step === 1 && notCentAccountAndNotSubscription) ||
        (step === 2 && multipleAccountsOrCheckSubscription)) &&
      !oneCurrencyOnly

    const passwordStep =
      ((step === 3 && multipleAccountsOrCheckSubscription) ||
        (step === stepTwoOrOne && notCentAccountAndNotSubscription) ||
        (step === 1 && isCentAccount({__typename: accountType}))) &&
      !moreDetails

    const detailsStep =
      ((step === stepThreeOrTwo && accountCategoryList.length > 1) ||
        (step === stepTwoOrOne && notCentAccountAndNotSubscription)) &&
      moreDetails

    let maxStep = isCentAccount({__typename: accountType})
      ? 2
      : get(accountCategoryList, 'length') > 1
      ? 4
      : 3
    maxStep = !oneCurrencyOnly ? maxStep : maxStep - 1

    return (
      <FullScreenDialog fullScreen desktopOnly open={true} onClose={() => this.decreaseStep()}>
        <PageTitle
          onBack={() => this.decreaseStep()}
          title={t(messages.addNewAccount.i18nKey, messages.addNewAccount.defaults)}
        />
        <PageBody>
          <Grid
            item
            xs={12}
            sm={categoryStep && checkSubscription ? false : 8}
            lg={categoryStep && checkSubscription ? false : 6}
          >
            <span>
              {/**@ts-ignore*/}
              <MobileStepper
                variant="progress"
                steps={maxStep + 1}
                position="static"
                activeStep={step + 1}
                className={classes.stepper}
                classes={{progress: classes.stepper}}
              />
              <Typography variant="caption">
                {step + 1}/{maxStep}
              </Typography>
            </span>
            {isAccountTypeStep && (
              <AccountTypeStep
                viewer={viewer}
                allowedAccountTypes={allowedAccounts}
                onSelectAccountType={this.chooseAccount.bind(this)}
              />
            )}
            {categoryStep && !checkSubscription && (
              <div>
                <PageSubTitle>
                  <Trans {...messages.selectAccountType} />
                </PageSubTitle>
                {categoryList}
              </div>
            )}
            {categoryStep && checkSubscription && (
              <div>
                <PageSubTitle>
                  <Trans {...messages.selectPlan} />
                </PageSubTitle>
                <ChangePlan
                  onCardClick={(plan) =>
                    this.chooseAccount(accountType, get(plan, 'accountSubtype'))
                  }
                />
              </div>
            )}
            {currenciesStep && (
              <div>
                <PageSubTitle>
                  <Trans {...messages.selectCurrency} />
                  {checkSubscription && (
                    <Typography variant="caption">
                      <Trans {...messages.tradingAccountCurrency} />
                    </Typography>
                  )}
                </PageSubTitle>
                {currencyList}
              </div>
            )}
            {passwordStep && this.renderForm()}
            {detailsStep && this.renderDetailsForm()}
            {missingDocs && detailsStep && this.renderPartnerMessageNotification()}
          </Grid>
          {accountType &&
            accountTypes[accountType].kycRequired &&
            get(viewer, 'kycStatus') !== kycStatuses.approved.value && (
              <AlertDialog
                open
                onClose={() => this.setState({accountType: '', step: 0})}
                agreeText={t(
                  messages[missingDocs ? 'verifyAccount' : 'close'].i18nKey,
                  messages[missingDocs ? 'verifyAccount' : 'close'].defaults,
                )}
                onAgree={() =>
                  missingDocs
                    ? history.push('/settings/profile')
                    : this.setState({accountType: '', step: 0})
                }
                disagreeText={
                  missingDocs ? t(messages.cancel.i18nKey, messages.cancel.defaults) : ''
                }
              >
                <Grid container>
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      <Trans
                        {...messages[
                          missingDocs ? 'accountKycRequiredMsg' : 'accountKycRequiredPendingMsg'
                        ]}
                        values={{account: accountTypes[accountType].label}}
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </AlertDialog>
            )}

          {this.state.showPasswordUpdatePopup && (
            <AlertDialog
              open
              onClose={() => {
                this.setState({showPasswordUpdatePopup: false}, () => {
                  history.push('/accounts')
                })
              }}
              title="Update Password First"
              agreeText="Update Password"
              onAgree={() => {
                this.setState({showPasswordUpdatePopup: false}, () => {
                  history.push('/settings/other-settings')
                })
              }}
              disagreeText="Cancel"
            >
              <Grid container>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    <Trans {...messages.passwordUpdatePopupContent} />
                  </Typography>
                </Grid>
              </Grid>
            </AlertDialog>
          )}
        </PageBody>
      </FullScreenDialog>
    )
  }
}

export default compose(
  withNamespaces(),
  withStyles(styles),
  graphql(CLIENT_DATA_QUERY, {
    props: ({data: {error, loading}, data}: any) => {
      const viewer = get(data, 'viewer')
      return {
        error,
        loading,
        viewer,
        socialSignup: !!get(viewer, 'oauthId'),
      }
    },
  }),
  graphql(ADD_ACCOUNT_MUTATION, {
    name: 'addAccountMutation',
    options: {
      refetchQueries: [{query: CLIENT_DATA_QUERY}],
    },
  }),
)(AddAccount)
